/**
 * P3-01: GameButton —— ButtonModern 模型的 React 抽象
 *（core.js:1576-1632；基类 Button core.js:867-1166）。
 *
 * 模型字段 → props 映射（fetchModel 数据流不变：tab 组件照旧调旧 controller
 * 的 fetchModel 产出 model，逐字段直传本组件，见 P3-02 测试）：
 * - model.name            → name（btnTitle 的 innerHTML 语义，引擎生成 HTML，不转义）
 * - model.description     → description（domNode 的 aria-description，core.js:976；
 *                          旧 btnContent 的 title 在现代按钮被清空，core.js:1584，故不透出）
 * - model.visible         → visible（display block/none，core.js:908-923/974）
 * - model.enabled         → enabled（.disabled，core.js:925-944/998-1000）
 * - model.resourceIsLimited → resourceIsLimited（仅在 disabled 时叠加 .limited，
 *                          core.js:940-942；enabled 时两个 class 都移除）
 * - model.twoRow          → twoRow（marginLeft/Right auto，core.js:980-983）
 * - model.*Link           → links（renderLinks/updateLink 的抽象，core.js:1612-1627：
 *                          sell 链、togglableOnOff 开关、toggleAutomation 自动化标记
 *                          "A"/"*"（auto-on/auto-off）等都是同一种 link）
 * - ButtonModernHelper.getTooltipHTML → getTooltip（tooltip 内容回调，
 *                          由 tab 侧直传 fetchModel/fetchExtendedModel 产物）
 * - model.hasResourceHover → getSelectedObject（mouseover 时
 *                          game.setSelectedObject(obj, domNode)，mouseout 时
 *                          game.clearSelectedObject()，core.js:1586-1595）
 *
 * 交互对齐：
 * - 点击 → animate（旧 jQuery opacity 0.5↔1 各 70ms，core.js:1009-1019，
 *   此处用 WAAPI 等价）+ onClick（tab 侧直调 controller.buyItem(model, event)，
 *   引擎内部自守 cannot-afford/not-enabled，core.js:2126-2151，组件不做 enabled 拦截）；
 * - Enter 键 → 同点击（core.js:1030-1034）；tabIndex=0（core.js:977）；
 * - link 点击：stopPropagation + preventDefault + animate + handler
 *   （core.js:1105-1119）；title.length > 4 加 "small" class（core.js:1093）；
 *   visible === false 时 display:none（core.js:1625）。
 *
 * 已知差异（留待首个 tab 迁移时按视觉比对结论处置）：
 * - 旧 ButtonModern tooltip 定位在按钮右侧 300px（attachTooltip(game, domNode,
 *   0, 300, ...)，core.js:1605 + 2666-2698）；本组件锚点即按钮本身，定位遵循
 *   新 TooltipProvider 约定（锚点下方 + 防出界钳制，见 TooltipProvider 文件头）。
 * - 旧 attachTooltip 的 Space 键展示 tooltip（core.js:2711-2719）未移植，
 *   TooltipProvider 体系整体无此约定。
 */
import { useCallback } from "react";
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { getGame } from "../bridge/GameBridge";
import { TooltipAnchor } from "./Tooltip/TooltipProvider";
import type { TooltipContentProvider } from "./Tooltip/TooltipProvider";

// ---------------------------------------------------------------------------
// 模型类型
// ---------------------------------------------------------------------------

/** renderLinks/updateLink 的模型抽象（core.js:1616-1627）；sell/开关/自动化标记同构 */
export interface GameButtonLinkModel {
  /** 链接标识，addLinkList dropdown 用（如 "assign"/"unassign"） */
  id?: string;
  /** 链接文本（innerHTML 语义，core.js:1096/1622） */
  title: string;
  /** 附加 class（如 bld-on/bld-off、auto-on/auto-off，core.js:1623） */
  cssClass?: string;
  /** 原生 title（core.js:1624） */
  tooltip?: string;
  /** undefined/true 显示，false → display:none（core.js:1625） */
  visible?: boolean;
  handler: (event: MouseEvent) => void;
}

export interface GameButtonProps {
  /** model.name：btnTitle 的 innerHTML（引擎生成 HTML，与旧版同等信任级别） */
  name: string;
  /** model.description：外层 aria-description */
  description?: string;
  /** model.visible：默认 true；false → display:none */
  visible?: boolean;
  /** model.enabled：默认 true；false → .disabled */
  enabled?: boolean;
  /** model.resourceIsLimited：truthy 且 disabled 时叠加 .limited */
  resourceIsLimited?: boolean | string;
  /** model.twoRow：marginLeft/Right auto */
  twoRow?: boolean;
  /** 链接组（sell/开关/自动化标记等），渲染于 btnContent 内 btnTitle 之后 */
  links?: GameButtonLinkModel[];
  /** tooltip 内容回调（ButtonModernHelper.getTooltipHTML 等价物，tab 侧直传） */
  getTooltip?: TooltipContentProvider;
  /** 点击回调（tab 侧直调 controller.buyItem(model, event)） */
  onClick?: (event: MouseEvent) => void;
  /** hasResourceHover：提供后 mouseover/mouseout 接 game.setSelectedObject/clearSelectedObject */
  getSelectedObject?: () => unknown;
  /**
   * P3-09：追加在 domNode class 之后（建筑按钮的 hasSellLink/small-text/
   * bldEnabled/bldlackResConvert 等旧 class 钩子，core.js:1924-1933/2024-2032）
   */
  domClassName?: string;
  /** P3-09：合并进 domNode 行内样式（val>9 的 font-size:90%，core.js:2020-2022） */
  domStyle?: CSSProperties;
  /**
   * P3-09：btnContent 内追加内容，渲染于 links 之后（建筑按钮的开关
   * dropdown 链接组等需要精确 DOM 顺序的复合链接；简单链接仍用 links）
   */
  children?: ReactNode;
}

// ---------------------------------------------------------------------------
// 价格行 HTML（ButtonModernHelper._renderPriceLine，core.js:1510-1540）
// ---------------------------------------------------------------------------

/** ButtonModernController.createPriceLineModel 产物的最小面（core.js:1277-1314） */
export interface PriceLineModel {
  title: string;
  displayValue: string;
  hasResources: boolean;
  indent?: number;
  children?: PriceLineModel[];
}

/**
 * 逐结构对齐 _renderPriceLine：price-block 容器（overflow:hidden），
 * 名称 span 左浮（padding-right:10px），价格 span 右浮（!hasResources → noRes）；
 * children 递归展开，名称按 indent 前缀 "&nbsp;&nbsp;&nbsp;" 并加 rawRes class。
 * 返回 HTML 字符串，供 tab 侧组装 tooltip 内容（与旧版同等信任级别，不转义）。
 */
export function renderPriceLinesHTML(prices: PriceLineModel[]): string {
  let html = "";
  for (const price of prices) {
    html += renderPriceLine(price, false, 0);
  }
  return html;
}

function renderPriceLine(price: PriceLineModel, asRaw: boolean, rawIndent: number): string {
  const nameStyle = "float: left; padding-right: 10px;";
  const priceClass = price.hasResources ? "" : "noRes";
  let html =
    "<div class=\"price-block\" style=\"overflow: hidden;\">" +
    "<span style=\"" + nameStyle + "\"" + (asRaw ? " class=\"rawRes\"" : "") + ">" +
    "&nbsp;&nbsp;&nbsp;".repeat(rawIndent) +
    price.title +
    "</span>" +
    "<span class=\"" + priceClass + "\" style=\"float: right;\">" +
    price.displayValue +
    "</span></div>";
  if (price.children && price.children.length) {
    for (const child of price.children) {
      // 旧版：子行名称前缀 indent 个 "&nbsp;&nbsp;&nbsp;" 并标记 rawRes（core.js:1528-1537）
      html += renderPriceLine(child, true, child.indent ?? 0);
    }
  }
  return html;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

/** 旧 animate：opacity 0.5→1 各 70ms（core.js:1009-1019）；WAAPI 等价 */
function animateButton(el: Element | null): void {
  if (el instanceof HTMLElement && typeof el.animate === "function") {
    el.animate([{ opacity: 0.5 }, { opacity: 1 }], { duration: 140 });
  }
}

/**
 * addLink/updateLink 的单链接渲染（core.js:1091-1127, 1616-1627）。
 * P3-09 导出：建筑按钮需要把 sell/开关/自动化链接与 dropdown 链接组按旧
 * DOM 顺序混排（经 GameButton children），复用同一渲染逻辑。
 */
export function GameButtonLink({ link }: { link: GameButtonLinkModel }) {
  const linkClass =
    (link.title.length > 4 ? "small" : "") + (link.cssClass ? " " + link.cssClass : "");
  const linkStyle: CSSProperties = {
    paddingLeft: "2px",
    float: "right",
    cursor: "pointer",
    display: link.visible === false ? "none" : "",
  };
  return (
    <a
      href="#"
      className={linkClass}
      style={linkStyle}
      title={link.tooltip}
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        animateButton((e.currentTarget as HTMLElement).closest(".btn"));
        link.handler(e);
      }}
      dangerouslySetInnerHTML={{ __html: link.title }}
    />
  );
}

export function GameButton({
  name,
  description,
  visible = true,
  enabled = true,
  resourceIsLimited,
  twoRow,
  links,
  getTooltip,
  onClick,
  getSelectedObject,
  domClassName,
  domStyle,
  children,
}: GameButtonProps) {
  // core.js:925-944：enabled 时两个 class 都移除；disabled 时加 disabled，
  // 且 resourceIsLimited truthy 才叠加 limited
  const className =
    "btn nosel modern" +
    (enabled ? "" : " disabled") +
    (!enabled && resourceIsLimited ? " limited" : "") +
    (domClassName ?? "");

  // core.js:971-983
  const style: CSSProperties = {
    position: "relative",
    display: visible ? "block" : "none",
    ...domStyle,
  };
  if (twoRow) {
    style.marginLeft = "auto";
    style.marginRight = "auto";
  }

  const handleClick = useCallback(
    (e: MouseEvent) => {
      animateButton(e.currentTarget);
      onClick?.(e);
    },
    [onClick],
  );

  // core.js:1030-1034
  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        animateButton(e.currentTarget);
        onClick?.(e as unknown as MouseEvent);
      }
    },
    [onClick],
  );

  // core.js:1586-1595（hasResourceHover）
  const handleMouseOver = useCallback(
    (e: MouseEvent) => {
      if (!getSelectedObject) {
        return;
      }
      getGame()?.setSelectedObject(getSelectedObject(), e.currentTarget);
    },
    [getSelectedObject],
  );
  const handleMouseOut = useCallback(() => {
    if (!getSelectedObject) {
      return;
    }
    getGame()?.clearSelectedObject();
  }, [getSelectedObject]);

  return (
    <TooltipAnchor
      className={className}
      style={style}
      tabIndex={0}
      ariaDescription={description}
      getTooltip={getTooltip}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      onMouseOver={getSelectedObject ? handleMouseOver : undefined}
      onMouseOut={getSelectedObject ? handleMouseOut : undefined}
    >
      <div className="btnContent">
        <span className="btnTitle" dangerouslySetInnerHTML={{ __html: name }} />
        {links?.map((link, i) => (
          <GameButtonLink key={i} link={link} />
        ))}
        {children}
      </div>
    </TooltipAnchor>
  );
}
